import { useState } from 'react';

import { getSearchParams } from '@vimeo/viewer-showcase-ui/utils/helper';

import { useLeadCaptureRegistration } from './hooks/use-lead-capture-registration';
import { ShowcaseAuthErrorCode, useShowcaseAuth } from './hooks/use-showcase-auth';
import { useSSGShowcaseSignature } from './hooks/use-ssg-showcase-signature';
import { BrandedLoginPage } from './pages/BrandedLoginPage';
import { ErrorPage } from './pages/ErrorPage';
import { LeadCapturePage } from './pages/LeadCapturePage';
import { LoadingPage } from './pages/LoadingPage';
import { PasswordPage } from './pages/PasswordPage';
import { UserNotAllowedPage } from './pages/UserNotAllowedPage';
import { AlbumStoreProvider } from './state/albumStore';
import { ViewerShowcases } from './ViewerShowcases';

interface ViewerShowcasesAuthorizationProps {
  albumIdOrUrl: number | string; // This could be an album ID or the custom album URL
  isShowcaseEmbedded?: boolean;
}

/**
 * The entry point of Viewer Showcases Feature.
 * This component is responsible for checking the authorization of the viewer to access the showcase.
 * If the viewer is authorized, it will render the ViewerShowcases component.
 * If the viewer is not authorized, it will render the appropriate views based on the error code.
 */
export const ViewerShowcasesAuthorization = ({
  albumIdOrUrl,
  isShowcaseEmbedded = false,
}: ViewerShowcasesAuthorizationProps) => {
  const showcaseHash = getSearchParams().get('sh');
  const { data, error, isLoading, mutate } = useShowcaseAuth(albumIdOrUrl, { isShowcaseEmbedded });
  const albumId = data?.metadata?.id || error?.res?.metadata?.id; // Extract the album ID from the response (we will always work with the ID in album store instead of custom URL)
  const { registrationEnabled, registrationLoading, setLeadUuid } =
    useLeadCaptureRegistration(albumId);
  const albumSignature = useSSGShowcaseSignature(albumId);

  /** Handle third part cookie disabled */
  const [isPasswordSuccess, setIsPasswordSuccess] = useState(false);

  if (error?.status === 404) {
    return <ErrorPage />;
  }

  if (isLoading || (!data && !error) || registrationLoading || !albumId) {
    return <LoadingPage />;
  }

  const isOffsite =
    typeof window !== undefined &&
    (window.location.hostname === data?.metadata?.domain ||
      window.location.hostname === error?.res?.metadata?.domain);

  if (error) {
    switch (error?.res?.errorCode) {
      case ShowcaseAuthErrorCode.passwordPrivacy: // 0
        return (
          <AlbumStoreProvider albumId={albumId} isShowcaseEmbedded={isShowcaseEmbedded}>
            <PasswordPage
              isCookiesDisabled={isPasswordSuccess} // If password is success, we mutate useShowcaseAuth but still get to this step to render password view, likely the cookies is disabled
              onSuccess={() => {
                setIsPasswordSuccess(true);
                mutate();
              }}
            />
          </AlbumStoreProvider>
        );

      case ShowcaseAuthErrorCode.teamPrivacy: // 2
        return (
          <AlbumStoreProvider
            albumId={albumId}
            isShowcaseEmbedded={isShowcaseEmbedded}
            isOffsite={isOffsite}
          >
            <BrandedLoginPage {...(error?.res?.metadata ?? {})} />
          </AlbumStoreProvider>
        );

      case ShowcaseAuthErrorCode.teamPrivacyLoggedInUser: // 3
        return <UserNotAllowedPage {...(error?.res?.metadata ?? {})} />;

      case ShowcaseAuthErrorCode.embedOnly: // 4
      case ShowcaseAuthErrorCode.unknownReason: // 5
      case ShowcaseAuthErrorCode.unlisted: // 6
      default:
        return <ErrorPage />;
    }
  }

  if (registrationEnabled) {
    return <LeadCapturePage albumId={albumId.toString()} setLeadUuid={setLeadUuid} />;
  }

  return (
    <AlbumStoreProvider
      albumId={albumId}
      albumSignature={albumSignature}
      hashedPass={data?.hashedPass || ''}
      isShowcaseEmbedded={isShowcaseEmbedded}
      isOffsite={isOffsite}
      showcaseHash={showcaseHash || ''}
    >
      <ViewerShowcases />
    </AlbumStoreProvider>
  );
};
